import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ChartModule } from 'primeng/chart';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { TranslocoService } from '@jsverse/transloco';
import { combineLatest } from 'rxjs';
import { DecimalPipe } from '@angular/common';

@Component({
  selector: 'app-gas-cost-analysis-chart',
  standalone: true,
  imports: [ChartModule],
  providers: [DecimalPipe],
  templateUrl: './gas-cost-analysis-chart.component.html',
  styleUrl: './gas-cost-analysis-chart.component.css'
})
export class GasCostAnalysisChartComponent implements OnInit, OnChanges {
  data: any;
  options: any;

  // Dummy data
  @Input() showDummyData: boolean = false;
  dummylineCostTrendData: number[] = [120, 123.28];
  dummybarSmcData: number[] = [12.182, 13.182];
  dummySmcDifference: number = 0;
  dummycostDifference: number = 0;

  periodLabels: string[] = ['Previous Period', 'Current Period'];

  labelCostTrend: string = "Total Costs"

  @Input() lineCostTrendData: number[] = [120, 123.28];
  @Input() barSmcData: number[] = [12.182, 13.182];
  @Input() inputedPeriods: string[] | undefined = undefined;
  @Input() SmcDifference: number = 1;
  @Input() costDifference: number = 3.28;

  constructor(private translocoService: TranslocoService, private decimalPipe: DecimalPipe) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.inputedPeriods === undefined) {
      const monthKeys = [
        'dashboard.period.previous',
        'dashboard.period.current',
      ];

      combineLatest(monthKeys.map((key) => this.translocoService.selectTranslate(key))).subscribe((translations) => {
        this.periodLabels = translations;
        this.updateChart();
      });
    }
    else {
      this.periodLabels = this.inputedPeriods;
    }
    this.updateChart();
  }

  ngOnInit(): void {
    this.updateChart();

    this.translocoService.selectTranslate('dashboard.total_costs_label').subscribe((translation) => {
      this.labelCostTrend = translation;
    });

    const monthKeys = [
      'dashboard.period.previous',
      'dashboard.period.current',
    ];

    if (this.inputedPeriods === undefined) {
      combineLatest(monthKeys.map((key) => this.translocoService.selectTranslate(key))).subscribe((translations) => {
        this.periodLabels = translations;
        this.updateChart();
      });
    }
    else {
      this.periodLabels = this.inputedPeriods;
    }
  }

  updateChart(): void {
    if (this.showDummyData) {
      this.lineCostTrendData = this.dummylineCostTrendData;
      this.barSmcData = this.dummybarSmcData;
      this.SmcDifference = this.dummySmcDifference;
      this.costDifference = this.dummycostDifference;
    }

    Chart.register(ChartDataLabels);

    this.data = {
      labels: this.periodLabels,
      datasets: [
        {
          type: 'line',
          label: this.labelCostTrend,
          data: this.lineCostTrendData,
          borderColor: '#6B8F8C',
          borderWidth: 2,
          tension: 0.4,
          pointStyle: 'circle',
          pointRadius: 6,
          pointHoverRadius: 8,
          pointBackgroundColor: '#EEB269',
          datalabels: {
            display: 'auto',
            align: 'start',
            anchor: 'start',
            color: '#000',
            font: {
              size: 10,
              weight: 'bold',
              family: 'Gilroy',
            },
            formatter: (value: number, context: any) => {
              if (context.dataIndex === 1) {
                const SmcSign = this.SmcDifference >= 0 ? '+' : '-';
                const costSign = this.costDifference >= 0 ? '+' : '-';

                const SmcDifference = Math.abs(this.SmcDifference);
                const costDifference = Math.abs(this.costDifference);

                return `${SmcSign} ${this.decimalPipe.transform(SmcDifference, '1.0-0') || SmcDifference.toFixed(2)} smc\n${costSign} ${this.decimalPipe.transform(costDifference, '1.0-0') || costDifference.toFixed(2)}€`;
              }
              return '';
            }
          },
          xAxisID: 'x2',
          yAxisID: 'y2',
        },
        {
          type: 'bar',
          label: 'smc',
          data: this.barSmcData,
          backgroundColor: ['#CBDCD6', '#7EA4A4'],
          borderRadius: 12,
          datalabels: {
            display: 'auto',
            align: 'center',
            anchor: 'center',
            color: '#000',
            font: {
              size: 12,
              weight: 'normal',
              family: 'Gilroy',
            },
            formatter: (value: number) => `${this.decimalPipe.transform(value, '1.0-0') || value} smc`,
          },
        },
      ],
    };

    this.options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const index = context.dataIndex;
              let label = context.dataset.label || '';
              let value = context.raw || 0;
              return `${label}: ${this.decimalPipe.transform(value, '1.0-0') || value}`;
            }
          }
        },
      },
      scales: {
        x: {
          stacked: false,
          display: false,
        },
        y: {
          beginAtZero: true,
          grid:
          {
            display: true,
          },
          border: {
            dash: [5, 5],
          },
          ticks: {
            display: false,
          },
        },
        x2: {
          beginAtZero: false,
          display: false,
        },
        y2: {
          beginAtZero: true,
          display: false,
        }
      },
    };
  }
}
